import { Button, Form, Input, message, Modal, Tooltip } from 'antd';
import { useState } from 'react';
import { useAuth } from '../../store/hooks';
import { UserOutlined } from '@ant-design/icons';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';

const UserSettingsModal: React.FC = () => {
    const { currentUser: user, loading } = useAuth();
    const [form] = Form.useForm();
    const [open, setOpen] = useState<boolean>(false);

    const openModal = () => setOpen(true);
    const closeModal = () => {
        setOpen(false);
        form.resetFields();
    };

    const validatePassword = (_: any, value: string) => {
        if (!value || value.length < 8 || !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
            return Promise.reject(new Error('Password must be at least 8 characters long and contain a special symbol'));
        }
        return Promise.resolve();
    };

    const compareToFirstPassword = (_: any, value: string) => {
        if (value !== form.getFieldValue('newPassword')) {
            return Promise.reject(new Error('The two passwords do not match'));
        }
        return Promise.resolve();
    };

    const onFinish = async (values: { currentPassword: string; newPassword: string }) => {
        if (!user || !user.email) {
            message.error("No user is currently logged in.");
            return;
        }

        try {
            const credential = EmailAuthProvider.credential(user.email, values.currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, values.newPassword);
            message.success("Password updated successfully.");
            closeModal();
        } catch (error: any) {
            console.error(error);
            message.error(error.message || "Failed to update password.");
        }
    };

    return (
        <>
            <Tooltip title="User Settings">
                <Button
                    type="default"
                    icon={<UserOutlined />}
                    onClick={openModal}
                    style={{ padding: 0, height: 32, display: 'flex', alignItems: 'center' }}
                />
            </Tooltip>
            <Modal
                title="Change Password"
                width={400}
                open={open}
                onCancel={closeModal}
                footer={null}
            >
                <Form form={form} onFinish={onFinish} layout="vertical">
                    <Form.Item
                        label="Current Password"
                        name="currentPassword"
                        rules={[{ required: true, message: 'Please enter your current password' }]}
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item
                        label="New Password"
                        name="newPassword"
                        rules={[
                            { required: true, message: 'Please enter your new password' },
                            { validator: validatePassword },
                        ]}
                        hasFeedback
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item
                        label="Confirm New Password"
                        name="confirm"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Please confirm your new password' },
                            { validator: compareToFirstPassword },
                        ]}
                        hasFeedback
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Update Password
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default UserSettingsModal;